<template>
  <div class="workbench-v2" :class="{ 'is-mobile': isMobile, 'in-chat': isMobile && mobileView === 'chat' }">
    <!-- 手机端：全屏对话页 -->
    <div v-if="isMobile && mobileView === 'chat'" class="mobile-chat-page">
      <header class="m-chat-header">
        <button type="button" class="m-back" @click="backToList">‹</button>
        <div class="m-chat-info">
          <div class="m-chat-name">{{ currentConversation && currentConversation.visitor_label ? currentConversation.visitor_label : '访客' }}</div>
          <div class="m-chat-meta">#{{ currentConversationId }} · {{ currentConversation ? statusLabel(currentConversation.status) : '' }}</div>
        </div>
        <button type="button" class="m-more" @click="mobileActionVisible = true">⋯</button>
        <span v-if="unreadTotal && mobileView === 'chat'" class="m-chat-unread-hint" @click="backToList">{{ unreadTotal }} 条未读</span>
      </header>

      <div ref="mobileMsgBox" class="m-msg-box">
        <div v-for="item in messages" :key="item.id" class="msg-row" :class="{ self: item.sender_type === 'agent', system: item.sender_type === 'system' }">
          <div class="msg-bubble">
            <div class="msg-body">
              <div v-if="item.content_type === 'text'" class="msg-content">{{ item.content }}</div>
              <template v-else>
                <img v-if="item.content_type === 'image' && item.file_url" :src="mediaUrl(item.file_url)" class="msg-media img" alt="" />
                <video v-else-if="item.content_type === 'video' && item.file_url" :src="mediaUrl(item.file_url)" class="msg-media video" controls />
                <a v-else-if="item.content_type === 'file' && item.file_url" :href="mediaUrl(item.file_url)" target="_blank" rel="noopener" class="msg-file">{{ item.content || '下载附件' }}</a>
                <div v-if="showMediaCaption(item)" class="msg-content msg-caption">{{ item.content }}</div>
              </template>
            </div>
            <div class="msg-time">{{ formatMsgTime(item.created_at) }}</div>
          </div>
        </div>
        <div v-if="currentConversationId && !messages.length" class="m-empty">暂无消息，发送第一条回复吧</div>
      </div>

      <div v-if="quickReplyList.length" class="m-quick-bar">
        <button
          v-for="item in quickReplyList"
          :key="'mqr-' + item.id"
          type="button"
          class="m-quick-chip"
          @click="appendReply(item.content)"
        >{{ item.title }}</button>
      </div>

      <footer class="m-input-bar">
        <input ref="mobileAgentFile" type="file" class="hidden-file" @change="onAgentFile" />
        <button type="button" class="m-attach" @click="$refs.mobileAgentFile.click()">＋</button>
        <textarea
          v-model="replyText"
          class="m-textarea"
          rows="1"
          :placeholder="replyPlaceholder"
          @keydown="handleInputKeydown"
        />
        <button type="button" class="m-send" :disabled="!replyText.trim()" @click="sendReply">发送</button>
      </footer>

      <el-dialog title="会话操作" :visible.sync="mobileActionVisible" width="92%" custom-class="m-action-dialog">
        <el-select v-model="transferTarget" placeholder="转接给同事" style="width:100%;margin-bottom:12px;">
          <el-option v-for="item in colleagueList" :key="item.id" :label="item.display_name" :value="item.id" />
        </el-select>
        <div style="display:flex;gap:10px;">
          <el-button style="flex:1;" @click="doMobileTransfer" :disabled="isClosedConversation || !transferTarget">转接</el-button>
          <el-button style="flex:1;" type="danger" @click="doMobileClose" :disabled="isClosedConversation">结束会话</el-button>
        </div>
      </el-dialog>
    </div>

    <!-- 手机端：会话列表页 -->
    <div v-else-if="isMobile" class="mobile-list-page">
      <header class="m-list-header">
        <div class="m-list-title">
          会话列表
          <span v-if="unreadTotal" class="m-unread-badge">{{ unreadTotal }}</span>
        </div>
        <div class="m-list-actions">
          <span class="ws-pill" :class="{ online: wsConnected }">{{ wsConnected ? '实时' : '轮询' }}</span>
          <button type="button" class="m-refresh" @click="loadConversations">刷新</button>
        </div>
      </header>

      <div class="m-list-head-row">
        <span class="h-id">ID</span>
        <span class="h-name">访客</span>
        <span class="h-status">状态</span>
        <span class="h-new">新消息</span>
        <span class="h-action">操作</span>
      </div>

      <div class="m-list-body">
        <div
          v-for="row in sortedFilteredConversations"
          :key="row.id"
          class="m-session-row"
          :class="{ unread: unreadMap[row.id] }"
        >
          <span class="c-id">#{{ row.id }}</span>
          <span class="c-name">{{ row.visitor_label || '访客' }}</span>
          <span class="c-status">
            <span class="m-status-tag" :class="'st-' + row.status">{{ statusLabel(row.status) }}</span>
          </span>
          <span class="c-new">
            <span v-if="unreadMap[row.id]" class="m-new-badge">
              <span class="m-new-bell">🔔</span>{{ unreadMap[row.id] }}
            </span>
            <span v-else class="m-new-none">-</span>
          </span>
          <button type="button" class="c-enter" :class="{ 'has-unread': unreadMap[row.id] }" @click="enterConversation(row)">进入</button>
        </div>
        <div v-if="!sortedFilteredConversations.length" class="m-list-empty">暂无会话</div>
      </div>
    </div>

    <!-- 桌面端 -->
    <div v-else class="layout">
      <aside class="side-menu">
        <div class="menu-title">客服工作台</div>
        <div class="menu-item" :class="{ active: activeModule === 'data' }" @click="activeModule = 'data'">数据概览</div>
        <div class="menu-item" :class="{ active: activeModule === 'sessions' }" @click="activeModule = 'sessions'">
          会话列表
          <el-badge v-if="unreadTotal" :value="unreadTotal" class="menu-badge" />
        </div>
      </aside>

      <section class="content">
        <template v-if="activeModule === 'data'">
          <el-row :gutter="12" class="summary-row">
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">连接状态</div>
                <div class="value" :class="{ online: wsConnected }">{{ wsConnected ? '实时在线' : '轮询模式' }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">全部会话</div>
                <div class="value">{{ conversations.length }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">进行中</div>
                <div class="value">{{ countByStatus('active') }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">未读提醒</div>
                <div class="value">{{ unreadTotal }}</div>
              </div>
            </el-col>
          </el-row>
          <el-card>
            <div slot="header" class="card-header">
              <div>
                <span>快捷回复</span>
                <span class="card-subhint">会话列表右侧聊天区也可直接使用</span>
              </div>
            </div>
            <div class="quick-reply-list">
              <el-button v-for="item in quickReplyList" :key="item.id" size="mini" plain @click="appendReply(item.content)">{{ item.title }}</el-button>
            </div>
          </el-card>
        </template>

        <template v-else>
          <el-row :gutter="12" class="session-row">
            <el-col :span="11">
              <el-card class="session-card">
                <div slot="header" class="card-header">
                  <span>会话列表</span>
                  <el-button size="mini" @click="loadConversations">刷新</el-button>
                </div>
                <div class="filters">
                  <el-input v-model="keyword" size="small" clearable placeholder="搜索会话ID或访客" />
                  <el-select v-model="statusFilter" size="small" placeholder="状态">
                    <el-option label="全部" value="all" />
                    <el-option label="进行中" value="active" />
                    <el-option label="排队中" value="queued" />
                    <el-option label="已结束" value="closed" />
                  </el-select>
                </div>
                <div class="sort-tools">
                  <el-switch v-model="prioritizeUnread" active-text="新消息优先置顶" inactive-text="按时间排序" active-color="#409eff" />
                </div>
                <el-table ref="convTable" :data="paginatedConversations" row-key="id" height="520" highlight-current-row @current-change="selectConversation">
                  <el-table-column prop="id" label="会话ID" width="78" />
                  <el-table-column prop="visitor_label" label="访客" min-width="120" show-overflow-tooltip />
                  <el-table-column label="状态" width="96">
                    <template slot-scope="{ row }">
                      <el-tag :type="statusTagType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="新消息" width="76">
                    <template slot-scope="{ row }">
                      <span v-if="unreadMap[row.id]" class="unread-bell">🔔</span>
                      <span v-else style="color:#909399;">-</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="created_at" label="创建时间" min-width="146" />
                  <el-table-column label="操作" width="96" fixed="right" align="right" header-align="right">
                    <template slot-scope="{ row }">
                      <div class="op-cell">
                        <el-button size="mini" type="primary" plain @click.stop="enterConversation(row)">进入会话</el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
                <div class="pager-wrap">
                  <el-pagination background layout="prev, pager, next" :page-size="pageSize" :current-page.sync="currentPage" :total="sortedFilteredConversations.length" @current-change="onPageChange" />
                </div>
              </el-card>
            </el-col>
            <el-col :span="13">
              <el-card class="chat-card">
                <div slot="header" class="chat-header">
                  <div>
                    <div class="chat-title">会话 {{ currentConversationId ? '#' + currentConversationId : '-' }}</div>
                    <div class="chat-subtitle">
                      <span v-if="currentConversation && currentConversation.visitor_label">访客：{{ currentConversation.visitor_label }}</span>
                      <span v-if="currentConversation"> | 创建：{{ currentConversation.created_at || '-' }}</span>
                      <span v-if="currentConversation"> | 分配：{{ currentConversation.assigned_at || '-' }}</span>
                      <span v-if="currentConversation && currentConversation.closed_at"> | 结束：{{ currentConversation.closed_at }}</span>
                    </div>
                  </div>
                  <div class="chat-actions">
                    <el-select v-model="transferTarget" placeholder="转接给" size="mini" style="width:150px;">
                      <el-option v-for="item in colleagueList" :key="item.id" :label="item.display_name" :value="item.id" />
                    </el-select>
                    <el-button size="mini" @click="transfer" :disabled="isClosedConversation || !currentConversationId">转接</el-button>
                    <el-button size="mini" type="danger" @click="closeCurrent" :disabled="isClosedConversation || !currentConversationId">结束</el-button>
                  </div>
                </div>
                <div ref="msgBox" class="msg-box">
                  <div v-for="item in messages" :key="item.id" class="msg-row" :class="{ self: item.sender_type === 'agent', system: item.sender_type === 'system' }">
                    <div class="msg-bubble">
                      <div class="msg-body">
                        <div v-if="item.content_type === 'text'" class="msg-content">{{ item.content }}</div>
                        <template v-else>
                          <img v-if="item.content_type === 'image' && item.file_url" :src="mediaUrl(item.file_url)" class="msg-media img" alt="" />
                          <video v-else-if="item.content_type === 'video' && item.file_url" :src="mediaUrl(item.file_url)" class="msg-media video" controls />
                          <a v-else-if="item.content_type === 'file' && item.file_url" :href="mediaUrl(item.file_url)" target="_blank" rel="noopener" class="msg-file">{{ item.content || '下载附件' }}</a>
                          <div v-if="showMediaCaption(item)" class="msg-content msg-caption">{{ item.content }}</div>
                        </template>
                      </div>
                      <div class="msg-time">{{ item.created_at || '' }}</div>
                    </div>
                  </div>
                  <el-empty v-if="currentConversationId && !messages.length" :image-size="80" description="暂无消息" />
                  <el-empty v-if="!currentConversationId" :image-size="80" description="请选择会话并点击进入" />
                </div>
                <div v-if="quickReplyList.length" class="quick-reply-inline">
                  <span class="quick-reply-label">快捷回复</span>
                  <div class="quick-reply-chips">
                    <el-button v-for="item in quickReplyList" :key="'qr-' + item.id" size="mini" plain type="primary" :disabled="!currentConversationId" @click="appendReply(item.content)">{{ item.title }}</el-button>
                  </div>
                </div>
                <div class="faq-inline" v-if="currentConversationId">
                  <span class="quick-reply-label">常见问题</span>
                  <div class="faq-inline-main">
                    <div class="faq-search-row">
                      <el-input v-model="faqQuery" size="mini" clearable placeholder="关键词匹配标题，空格表示多处都要匹配" style="width:260px;" @keyup.enter.native="runFaqSearch" />
                      <el-button size="mini" @click="runFaqSearch" :loading="faqSearchLoading">搜索</el-button>
                    </div>
                    <el-checkbox-group v-if="faqResults.length" v-model="faqSelectedIds" class="faq-check-group">
                      <el-checkbox v-for="item in faqResults" :key="'faq-' + item.id" :label="item.id">{{ item.title }}</el-checkbox>
                    </el-checkbox-group>
                    <div class="faq-push-row">
                      <el-button size="mini" type="success" plain @click="pushFaqToVisitor" :disabled="!faqSelectedIds.length">推送给访客选择</el-button>
                      <span class="faq-push-hint">访客将在聊天窗口看到可选标题，点选后查看答案</span>
                    </div>
                  </div>
                </div>
                <div class="input-area">
                  <input ref="agentFile" type="file" class="hidden-file" @change="onAgentFile" />
                  <el-input type="textarea" :rows="2" resize="none" v-model="replyText" :disabled="!currentConversationId" :placeholder="replyPlaceholder" @keydown.native="handleInputKeydown" />
                  <div class="send-area">
                    <span class="tips">会话列表内可直接点快捷回复填入输入框</span>
                    <div class="send-actions">
                      <el-button size="small" @click="$refs.agentFile.click()" :disabled="!currentConversationId">附件</el-button>
                      <el-button type="primary" @click="sendReply" :disabled="!currentConversationId">发送</el-button>
                    </div>
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import {
  heartbeat,
  listConversations,
  conversationMessages,
  replyConversation,
  closeConversation,
  transferConversation,
  quickReplies,
  colleagues,
  agentUpload,
  searchFaq,
  pushConversationFaq,
  API_BASE
} from '../api/agent';
import { WS_URL } from '../config/endpoints';
import { getToken } from '../utils/auth';

export default {
  data() {
    return {
      conversations: [],
      currentConversationId: null,
      messages: [],
      cursor: 0,
      replyText: '',
      quickReplyList: [],
      colleagueList: [],
      transferTarget: null,
      activeModule: 'sessions',
      keyword: '',
      statusFilter: 'all',
      prioritizeUnread: true,
      isMobile: false,
      mobileView: 'list',
      mobileActionVisible: false,
      currentPage: 1,
      pageSize: 10,
      unreadMap: {},
      convActivityMap: {},
      ws: null,
      wsConnected: false,
      timer: null,
      reconnectTimer: null,
      originalTitle: '',
      titleFlashTimer: null,
      titleFlashToggle: false,
      notifyAudioCtx: null,
      faqQuery: '',
      faqResults: [],
      faqSelectedIds: [],
      faqSearchLoading: false
    };
  },
  computed: {
    currentConversation() {
      return this.conversations.find(v => v.id === this.currentConversationId) || null;
    },
    isClosedConversation() {
      return !!(this.currentConversation && this.currentConversation.status === 'closed');
    },
    replyPlaceholder() {
      if (!this.currentConversationId) return '请先选择会话';
      if (this.isClosedConversation) return '会话已结束，留言将保存给访客';
      return this.isMobile ? '输入消息…' : '输入消息，Enter发送 / Shift+Enter换行';
    },
    filteredConversations() {
      return this.conversations.filter(item => {
        if (this.statusFilter !== 'all' && item.status !== this.statusFilter) return false;
        if (!this.keyword.trim()) return true;
        const q = this.keyword.trim();
        if (String(item.id).includes(q)) return true;
        const label = item.visitor_label ? String(item.visitor_label) : '';
        return label.toLowerCase().includes(q.toLowerCase());
      });
    },
    sortedFilteredConversations() {
      return [...this.filteredConversations].sort((a, b) => {
        if (this.prioritizeUnread) {
          const ua = Number(this.unreadMap[a.id] || 0);
          const ub = Number(this.unreadMap[b.id] || 0);
          if (ub !== ua) return ub - ua;
        }
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        if (tb !== ta) return tb - ta;
        return (b.id || 0) - (a.id || 0);
      });
    },
    paginatedConversations() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedFilteredConversations.slice(start, start + this.pageSize);
    },
    unreadTotal() {
      return Object.values(this.unreadMap).reduce((sum, v) => sum + Number(v || 0), 0);
    }
  },
  methods: {
    updateIsMobile() {
      const mobile = window.innerWidth <= 768;
      if (this.isMobile && !mobile && this.mobileView === 'chat') {
        this.mobileView = 'list';
      }
      this.isMobile = mobile;
      this.syncBodyScrollLock();
    },
    syncBodyScrollLock() {
      if (this.isMobile && this.mobileView === 'chat') {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      this.emitMobileChatMode();
    },
    emitMobileChatMode() {
      this.$emit('mobile-chat-change', this.isMobile && this.mobileView === 'chat');
    },
    formatMsgTime(ts) {
      if (!ts) return '';
      const s = String(ts);
      if (s.length >= 16) return s.slice(5, 16);
      return s;
    },
    backToList() {
      this.mobileView = 'list';
      this.mobileActionVisible = false;
      this.syncBodyScrollLock();
      this.emitMobileChatMode();
    },
    mediaUrl(path) {
      if (!path) return '';
      if (/^https?:\/\//i.test(path)) return path;
      return (API_BASE || '') + path;
    },
    showMediaCaption(item) {
      if (!item || !item.file_url) return false;
      if (item.content_type !== 'image' && item.content_type !== 'video') return false;
      const c = (item.content || '').trim();
      if (!c || c === '[附件]') return false;
      if (item.file_name && c === item.file_name) return false;
      return true;
    },
    async onAgentFile(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      input.value = '';
      if (!file || !this.currentConversationId) return;
      try {
        const res = await agentUpload(file);
        const meta = res.data && res.data.data;
        if (!meta || !meta.url) return;
        const body = {
          content_type: meta.content_type,
          content: meta.name || '[附件]',
          file_url: meta.url,
          file_name: meta.name || '',
          file_mime: meta.mime || '',
          file_size: meta.size || 0
        };
        const { data: r2 } = await replyConversation(this.currentConversationId, body);
        if (r2 && r2.data) this.upsertMessages([r2.data]);
        await this.loadMessages();
      } catch (err) {
        this.$message.error('上传或发送失败');
      }
    },
    countByStatus(status) {
      return this.conversations.filter(v => v.status === status).length;
    },
    statusTagType(status) {
      if (status === 'active') return 'success';
      if (status === 'queued') return 'warning';
      if (status === 'closed') return 'info';
      return '';
    },
    statusLabel(status) {
      if (status === 'active') return '进行中';
      if (status === 'queued') return '排队中';
      if (status === 'closed') return '已结束';
      return status || '-';
    },
    async loadConversations() {
      const { data } = await listConversations();
      const list = data.data || [];
      if (!this.wsConnected) {
        this.detectConversationActivityChanges(list);
      }
      this.conversations = list;
      this.syncConvActivityMap(list);
      this.ensurePageInRange();
      if (!this.conversations.length) {
        this.currentConversationId = null;
        this.messages = [];
        this.cursor = 0;
        if (this.isMobile) this.mobileView = 'list';
        return;
      }
      const exists = this.conversations.some(v => v.id === this.currentConversationId);
      if (!exists) {
        this.currentConversationId = null;
        if (this.isMobile) this.mobileView = 'list';
      }
      if (!this.currentConversationId && this.conversations.length && !this.isMobile) {
        await this.selectConversation(this.conversations[0]);
      }
    },
    async selectConversation(row) {
      if (!row) return;
      this.currentConversationId = row.id;
      this.$set(this.unreadMap, row.id, 0);
      this.cursor = 0;
      this.messages = [];
      this.faqQuery = '';
      this.faqResults = [];
      this.faqSelectedIds = [];
      await this.loadMessages();
    },
    async enterConversation(row) {
      await this.selectConversation(row);
      if (this.isMobile) {
        this.mobileView = 'chat';
        this.syncBodyScrollLock();
        this.$nextTick(this.scrollMsgToBottom);
        return;
      }
      this.activeModule = 'sessions';
      this.locateConversationPage(row.id);
      this.$nextTick(this.highlightCurrentRow);
    },
    async loadMessages() {
      if (!this.currentConversationId) return;
      const { data } = await conversationMessages(this.currentConversationId, this.cursor);
      const list = data.data || [];
      if (list.length) this.upsertMessages(list);
    },
    upsertMessages(list) {
      const map = {};
      this.messages.forEach(item => { map[item.id] = item; });
      list.forEach(item => { map[item.id] = item; });
      this.messages = Object.values(map).sort((a, b) => a.id - b.id);
      const last = this.messages[this.messages.length - 1];
      this.cursor = last ? last.id : this.cursor;
      this.$nextTick(this.scrollMsgToBottom);
    },
    scrollMsgToBottom() {
      const box = this.$refs.mobileMsgBox || this.$refs.msgBox;
      if (!box) return;
      box.scrollTop = box.scrollHeight;
    },
    appendReply(content) {
      this.replyText = this.replyText ? `${this.replyText}\n${content}` : content;
    },
    async runFaqSearch() {
      const q = this.faqQuery.trim();
      if (!q) {
        this.faqResults = [];
        return;
      }
      this.faqSearchLoading = true;
      try {
        const { data } = await searchFaq(q);
        this.faqResults = data.data || [];
        const idSet = new Set(this.faqResults.map(r => r.id));
        this.faqSelectedIds = this.faqSelectedIds.filter(id => idSet.has(id));
      } catch (e) {
        this.$message.error('常见问题搜索失败');
      } finally {
        this.faqSearchLoading = false;
      }
    },
    async pushFaqToVisitor() {
      if (!this.currentConversationId || !this.faqSelectedIds.length) return;
      try {
        await pushConversationFaq(this.currentConversationId, { faq_ids: this.faqSelectedIds });
        this.$message.success('已推送给访客');
      } catch (e) {
        this.$message.error('推送失败');
      }
    },
    handleInputKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendReply();
      }
    },
    async sendReply() {
      if (!this.currentConversationId || !this.replyText.trim()) return;
      const content = this.replyText;
      const { data } = await replyConversation(this.currentConversationId, { content });
      this.replyText = '';
      if (data && data.data) this.upsertMessages([data.data]);
      await this.loadMessages();
    },
    async transfer() {
      if (!this.currentConversationId || !this.transferTarget) return;
      await transferConversation(this.currentConversationId, this.transferTarget);
      this.currentConversationId = null;
      this.messages = [];
      this.cursor = 0;
      if (this.isMobile) this.mobileView = 'list';
      await this.loadConversations();
    },
    async closeCurrent() {
      if (!this.currentConversationId) return;
      await closeConversation(this.currentConversationId);
      this.currentConversationId = null;
      this.messages = [];
      this.cursor = 0;
      if (this.isMobile) this.mobileView = 'list';
      await this.loadConversations();
    },
    async doMobileTransfer() {
      await this.transfer();
      this.mobileActionVisible = false;
    },
    async doMobileClose() {
      await this.closeCurrent();
      this.mobileActionVisible = false;
    },
    markCurrentConversationRead() {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!this.currentConversationId) return;
      this.$set(this.unreadMap, this.currentConversationId, 0);
    },
    connectWs() {
      const token = getToken();
      if (!token) return;
      if (this.ws) {
        try { this.ws.close(); } catch (e) {}
      }
      this.ws = new WebSocket(WS_URL);
      this.ws.onopen = () => {
        this.wsConnected = false;
        this.ws.send(JSON.stringify({ action: 'auth', role: 'agent', token }));
      };
      this.ws.onclose = () => {
        this.wsConnected = false;
        this.scheduleReconnect();
      };
      this.ws.onerror = () => {
        this.wsConnected = false;
      };
      this.ws.onmessage = async evt => {
        const e = JSON.parse(evt.data || '{}');
        if (e.type === 'auth_ok') {
          this.wsConnected = true;
          return;
        }
        if (e.type === 'error') {
          this.wsConnected = false;
          return;
        }
        if (e.type === 'message_new' && e.conversation_id === this.currentConversationId) {
          const inCurrentChat = !this.isMobile || this.mobileView === 'chat';
          if (inCurrentChat) {
            this.upsertMessages([e.payload]);
            if (this.isMobile && e.payload && e.payload.sender_type === 'visitor') {
              this.vibrateMobile();
            }
          } else {
            await this.notifyNewMessage(e.conversation_id, e.payload);
          }
          return;
        }
        if (e.type === 'conversation_assigned' || e.type === 'conversation_closed') {
          await this.loadConversations();
          return;
        }
        if (e.type === 'message_new' && e.conversation_id !== this.currentConversationId) {
          await this.notifyNewMessage(e.conversation_id, e.payload);
          await this.loadConversations();
        }
      };
    },
    isInCurrentChat(conversationId) {
      return conversationId === this.currentConversationId && (!this.isMobile || this.mobileView === 'chat');
    },
    conversationLabel(conversationId) {
      const row = this.conversations.find(v => v.id === conversationId);
      if (row && row.visitor_label) return row.visitor_label;
      return '#' + conversationId;
    },
    messagePreview(payload) {
      if (!payload) return '新消息';
      if (payload.content_type === 'image') return '[图片]';
      if (payload.content_type === 'video') return '[视频]';
      if (payload.content_type === 'file') return '[附件]';
      const text = String(payload.content || '').trim();
      if (!text) return '新消息';
      return text.length > 24 ? text.slice(0, 24) + '…' : text;
    },
    vibrateMobile() {
      if (!this.isMobile || !navigator.vibrate) return;
      try {
        navigator.vibrate(60);
      } catch (e) {}
    },
    showMobileToast(conversationId, payload) {
      const label = this.conversationLabel(conversationId);
      const preview = this.messagePreview(payload);
      this.$message({
        message: label + '：' + preview,
        type: 'warning',
        duration: 3500,
        showClose: true
      });
    },
    async notifyNewMessage(conversationId, payload) {
      this.$set(this.unreadMap, conversationId, Number(this.unreadMap[conversationId] || 0) + 1);
      this.playNotifySound();
      this.vibrateMobile();
      if (this.isMobile) {
        this.showMobileToast(conversationId, payload);
      }
    },
    syncConvActivityMap(list) {
      list.forEach(conv => {
        this.convActivityMap[conv.id] = conv.updated_at || conv.created_at || '';
      });
    },
    detectConversationActivityChanges(list) {
      list.forEach(conv => {
        const id = conv.id;
        const ts = conv.updated_at || conv.created_at || '';
        const prev = this.convActivityMap[id];
        if (!prev || !ts || prev === ts) return;
        if (this.isInCurrentChat(id)) return;
        this.notifyNewMessage(id, null);
      });
    },
    scheduleReconnect() {
      if (this.reconnectTimer) return;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connectWs();
      }, 3000);
    },
    onPageChange() {
      this.$nextTick(this.highlightCurrentRow);
    },
    ensurePageInRange() {
      const totalPages = Math.max(1, Math.ceil(this.sortedFilteredConversations.length / this.pageSize));
      if (this.currentPage > totalPages) this.currentPage = totalPages;
      if (this.currentPage < 1) this.currentPage = 1;
    },
    locateConversationPage(conversationId) {
      const idx = this.sortedFilteredConversations.findIndex(item => item.id === conversationId);
      if (idx < 0) return;
      this.currentPage = Math.floor(idx / this.pageSize) + 1;
    },
    highlightCurrentRow() {
      if (!this.currentConversationId || !this.$refs.convTable) return;
      const row = this.paginatedConversations.find(item => item.id === this.currentConversationId);
      if (row) this.$refs.convTable.setCurrentRow(row);
    },
    playNotifySound() {
      try {
        if (!this.notifyAudioCtx) this.notifyAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = this.notifyAudioCtx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.value = 0.05;
        osc.start();
        osc.stop(ctx.currentTime + 0.12);
      } catch (e) {}
    },
    startTitleFlash() {
      if (this.titleFlashTimer) return;
      this.titleFlashTimer = setInterval(() => {
        this.titleFlashToggle = !this.titleFlashToggle;
        document.title = this.titleFlashToggle ? '【新消息】客服工作台' : this.originalTitle;
      }, 900);
    },
    stopTitleFlash() {
      if (this.titleFlashTimer) clearInterval(this.titleFlashTimer);
      this.titleFlashTimer = null;
      document.title = this.originalTitle;
    }
  },
  async created() {
    this.originalTitle = document.title || '客服工作台';
    document.addEventListener('visibilitychange', this.markCurrentConversationRead);
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);
    await heartbeat();
    await Promise.all([
      this.loadConversations(),
      quickReplies().then(r => { this.quickReplyList = r.data.data || []; }),
      colleagues().then(r => { this.colleagueList = r.data.data || []; })
    ]);
    this.connectWs();
    this.timer = setInterval(() => {
      heartbeat().catch(() => {});
      this.loadMessages().catch(() => {});
      this.loadConversations().catch(() => {});
    }, 2500);
    this.$nextTick(this.emitMobileChatMode);
  },
  watch: {
    unreadTotal(val) {
      if (val > 0) this.startTitleFlash();
      else this.stopTitleFlash();
    },
    statusFilter() {
      this.currentPage = 1;
      this.$nextTick(this.highlightCurrentRow);
    },
    keyword() {
      this.currentPage = 1;
      this.$nextTick(this.highlightCurrentRow);
    },
    currentConversationId(val) {
      if (!val || this.isMobile) return;
      this.locateConversationPage(val);
      this.$nextTick(this.highlightCurrentRow);
    }
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) this.ws.close();
    this.stopTitleFlash();
    document.removeEventListener('visibilitychange', this.markCurrentConversationRead);
    window.removeEventListener('resize', this.updateIsMobile);
    document.body.style.overflow = '';
    this.emitMobileChatMode();
  }
};
</script>

<style scoped>
.workbench-v2 {
  min-height: calc(100vh - 100px);
}

.workbench-v2.is-mobile {
  min-height: auto;
}

.layout {
  display: flex;
  gap: 12px;
}

.side-menu {
  width: 170px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  height: calc(100vh - 130px);
  padding: 12px 10px;
}

.menu-title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 12px;
}

.menu-item {
  position: relative;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: #606266;
  margin-bottom: 6px;
  transition: all .2s;
}

.menu-item:hover {
  background: #f5f7fa;
}

.menu-item.active {
  background: #ecf5ff;
  color: #409eff;
  font-weight: 600;
}

.menu-badge {
  position: absolute;
  right: 10px;
  top: 10px;
}

.content {
  flex: 1;
}

/* ========== 手机端：会话列表 ========== */
.mobile-list-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 56px);
  max-height: calc(100dvh - 56px);
  background: #f1f5f9;
}

.m-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 10px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.m-list-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.m-unread-badge {
  font-size: 12px;
  font-weight: 700;
  background: #ef4444;
  color: #fff;
  padding: 2px 7px;
  border-radius: 999px;
}

.m-list-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ws-pill {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #64748b;
  font-weight: 700;
}

.ws-pill.online {
  border-color: #86efac;
  color: #059669;
}

.m-refresh {
  border: none;
  background: #2563eb;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.m-list-head-row,
.m-session-row {
  display: grid;
  grid-template-columns: 48px 1fr 62px 52px 52px;
  gap: 6px;
  align-items: center;
  padding: 0 12px;
}

.m-list-head-row {
  height: 36px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.m-list-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
}

.m-session-row {
  min-height: 52px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.m-session-row.unread {
  background: #eff6ff;
}

.c-id {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.c-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
  font-weight: 500;
}

.m-status-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 6px;
  white-space: nowrap;
}

.m-status-tag.st-active {
  background: #dcfce7;
  color: #15803d;
}

.m-status-tag.st-queued {
  background: #fef9c3;
  color: #a16207;
}

.m-status-tag.st-closed {
  background: #f1f5f9;
  color: #64748b;
}

.c-enter {
  border: none;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 0;
  border-radius: 8px;
  cursor: pointer;
}

.c-enter.has-unread {
  background: #dc2626;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.35);
}

.c-new {
  text-align: center;
}

.m-new-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 2px 6px;
  border-radius: 999px;
}

.m-new-bell {
  font-size: 11px;
  animation: bell-shake 1.2s ease-in-out infinite;
}

.m-new-none {
  color: #cbd5e1;
  font-size: 13px;
}

.h-new {
  text-align: center;
}

.m-list-empty {
  text-align: center;
  padding: 48px 16px;
  color: #94a3b8;
  font-size: 14px;
}

/* ========== 手机端：对话页 ========== */
.mobile-chat-page {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  background: #eef2f7;
}

.m-chat-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background: linear-gradient(145deg, #1e293b, #0f172a);
  color: #fff;
}

.m-back,
.m-more {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.m-chat-info {
  flex: 1;
  min-width: 0;
}

.m-chat-name {
  font-size: 16px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-chat-meta {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 2px;
}

.m-chat-unread-hint {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  background: #ef4444;
  color: #fff;
  padding: 4px 8px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
}

.m-msg-box {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px;
}

.m-empty {
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  padding: 40px 16px;
}

.m-quick-bar {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  -webkit-overflow-scrolling: touch;
}

.m-quick-chip {
  flex-shrink: 0;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
}

.m-input-bar {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.m-attach {
  width: 40px;
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  font-size: 20px;
  color: #475569;
  cursor: pointer;
  flex-shrink: 0;
}

.m-textarea {
  flex: 1;
  min-height: 40px;
  max-height: 100px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 15px;
  line-height: 1.4;
  resize: none;
  font-family: inherit;
  outline: none;
}

.m-textarea:focus {
  border-color: #2563eb;
}

.m-send {
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 12px;
  background: #2563eb;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}

.m-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ========== 桌面端样式（保留） ========== */
.summary-row {
  margin-bottom: 12px;
}

.summary-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #ebeef5;
  padding: 10px 12px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
}

.summary-card .label {
  font-size: 12px;
  color: #909399;
}

.summary-card .value {
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

.summary-card .value.online {
  color: #1f9d5c;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-subhint {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
  margin-left: 8px;
}

.quick-reply-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filters {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 8px;
  margin-bottom: 10px;
}

.sort-tools {
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-end;
}

.pager-wrap {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.chat-card,
.session-card {
  min-height: 640px;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.chat-title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
}

.chat-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.chat-actions {
  display: flex;
  gap: 8px;
}

.msg-box {
  height: 348px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  background: #f8fafc;
}

.quick-reply-inline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0 4px;
  border-top: 1px solid #ebeef5;
}

.quick-reply-label {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
  line-height: 28px;
}

.quick-reply-chips {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.faq-inline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0 4px;
  border-top: 1px solid #ebeef5;
}

.faq-inline-main {
  flex: 1;
  min-width: 0;
}

.faq-search-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.faq-check-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  max-height: 140px;
  overflow-y: auto;
}

.faq-push-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.faq-push-hint {
  font-size: 12px;
  color: #909399;
}

.msg-row {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.msg-row.self {
  justify-content: flex-end;
}

.msg-row.system {
  justify-content: center;
}

.msg-row.system .msg-bubble {
  max-width: 92%;
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.55;
  text-align: center;
}

.msg-row.system .msg-time {
  display: none;
}

.msg-bubble {
  max-width: 82%;
  border-radius: 14px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.msg-row.self .msg-bubble {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.hidden-file {
  display: none;
}

.msg-body {
  line-height: 1.55;
}

.msg-content {
  line-height: 1.55;
  word-break: break-word;
}

.msg-media.img {
  max-width: min(220px, 70vw);
  border-radius: 8px;
  display: block;
}

.msg-media.video {
  max-width: min(240px, 75vw);
  border-radius: 8px;
  display: block;
}

.msg-caption {
  margin-top: 6px;
  font-size: 13px;
}

.msg-file {
  text-decoration: underline;
  word-break: break-all;
}

.msg-row.self .msg-file {
  color: #e8f1ff;
}

.msg-time {
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.7;
}

.send-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.input-area {
  margin-top: 10px;
}

.send-area {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tips {
  font-size: 12px;
  color: #909399;
}

.unread-bell {
  animation: bell-shake 1.2s ease-in-out infinite;
}

@keyframes bell-shake {
  0%, 100% { transform: rotate(0deg) scale(1); opacity: 1; }
  25% { transform: rotate(-12deg) scale(1.05); opacity: 0.85; }
  75% { transform: rotate(12deg) scale(1.05); opacity: 0.85; }
}
</style>
